import { Button } from '@design-system/Button';
import { ChevronRight } from 'lucide-react';
import { FormulaCard } from '@/components/screens/HomePage/components/FormulaCard';
import { Skeleton } from '@design-system/Skeleton';
import {
  SUPPLEMENT_BACKGROUND,
  ORAL_LOZENGES_BACKGROUND,
  GUT_PASTE_BACKGROUND,
  GUT_FORMULA_BACKGROUND,
} from '@/constants/images';
import { useGetHomeProductsViewModel } from '@/features/HomeViewModels/HomeProducts.view.model';
import { FoodProduct } from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';
import { useNavigate } from 'react-router-dom';
import { useManageSubscriptionLink } from '@/hooks/useManageSubscriptionLink';

const BACKGROUND_IMAGES: { [key: number]: string } = {
  1: ORAL_LOZENGES_BACKGROUND,
  2: SUPPLEMENT_BACKGROUND,
  3: GUT_FORMULA_BACKGROUND,
  4: GUT_PASTE_BACKGROUND,
};

export const HomeFormulas = () => {
  const navigate = useNavigate();
  const manageSubscriptionLink = useManageSubscriptionLink();
  const { homeProductsData, ishomeProductsDataLoading, ishomeProductsDataError} =
    useGetHomeProductsViewModel();

  const activeProductsList = homeProductsData?.filter(
    (item: FoodProduct) => item?.is_subscribed && !item?.is_addon,
  );
  const hasSubscribedProducts = activeProductsList && activeProductsList.length > 0;

  if (ishomeProductsDataLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 gap-1.5">
          {[1, 2, 3, 4].map((index) => (
            <Skeleton key={index} className="h-72 rounded" />
          ))}
        </div>
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (ishomeProductsDataError || !homeProductsData?.length) return;

  const formulaData = activeProductsList?.map((product) => ({
    title: product.name,
    description:
      'A one-of-a-kind, custom formula that includes all of your Viome-recommended supplement ingredients.',
    image: product.image_url,
    backgroundImage: BACKGROUND_IMAGES[product.id] || SUPPLEMENT_BACKGROUND,
    isSubscribed: product.is_subscribed,
  }));

  return (
    <div className="space-y-4">
      {hasSubscribedProducts && (
        <>
          <h2 data-testid="home-formulas-title" className="typography-h2 text-primary">My Formulas</h2>
          <div className="grid grid-cols-2 gap-1.5">
            {formulaData?.map((formula, index) => (
              <FormulaCard
                key={index}
                data-testid={`home-formulaCard-${formula.title.replace(/\s+/g, '-').toLowerCase() || index}`}
                {...formula}
              />
            ))}
          </div>

          <div className="flex justify-start w-full">
            <Button
              variant="secondary"
              data-testid="home-formulas-manageSubscriptionButton"
              className="w-full bg-white text-primary px-6 py-3 flex justify-between items-center"
              onClick={() => navigate(manageSubscriptionLink)}
            >
              <span className="typography-body-emphasis">Manage my subscription</span>
              <ChevronRight className="h-5 w-5" aria-hidden />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
